import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { authFetch } from '../../utils/authFetch';
import { useAuth } from '../../contexts/AuthContext';
import Notification from '../common/Notification';
import MonthEndDuesManager from './MonthEndDuesManager';
import '../../styles/Fees.css';
import { FaRupeeSign, FaUserGraduate, FaSearch, FaFilter, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaHistory, FaReceipt, FaCog, FaExternalLinkAlt } from 'react-icons/fa';

// Helper function to calculate total due amount
const calculateTotalDue = (previousDues, monthlyFee, examFee, transportFee, discount = 0) => {
  previousDues = parseFloat(previousDues) || 0;
  monthlyFee = parseFloat(monthlyFee) || 0;
  examFee = parseFloat(examFee) || 0;
  transportFee = parseFloat(transportFee) || 0;
  discount = parseFloat(discount) || 0;
  
  if (previousDues < 0) {
    // Student owes money, add the absolute value to total, then subtract discount
    return Math.abs(previousDues) + monthlyFee + examFee + transportFee - discount;
  } else {
    // Student has advance payment, subtract it from total, then subtract discount
    const total = monthlyFee + examFee + transportFee - previousDues - discount;
    // Ensure we don't go below zero
    return Math.max(0, total);
  }
};

const Fees = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [activeFilter, setActiveFilter] = useState('active');
  const [showActiveDropdown, setShowActiveDropdown] = useState(false);
  const [sortField, setSortField] = useState('Rno');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentMonthFee, setCurrentMonthFee] = useState(0);
  const [examinationFee, setExaminationFee] = useState(0);
  const [transportFee, setTransportFee] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [otherFees, setOtherFees] = useState([]);
  
  // Transaction tracking state
  const [isPendingTransaction, setIsPendingTransaction] = useState(false);
  const [feeAbortTime, setFeeAbortTime] = useState(5);
  const [countdown, setCountdown] = useState(5);
  const [, setPendingTransactionData] = useState(null);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [sendToWhatsApp, setSendToWhatsApp] = useState(true);
  const [todaysCollection, setTodaysCollection] = useState(0);
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));

  // Restrict receipt date picker to the current month only
  const today = new Date();
  const currentMonthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const currentMonthEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

  // Transaction history state
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Already-paid-this-month tracking
  const [alreadyPaidMonthly, setAlreadyPaidMonthly] = useState(false);
  const [alreadyPaidExam, setAlreadyPaidExam] = useState(false);
  const [alreadyPaidTransport, setAlreadyPaidTransport] = useState(false);
  const [loadingFees, setLoadingFees] = useState(false);

  // Exam fee months: March (3), July (7), November (11)
  const EXAM_FEE_MONTHS = [3, 7, 11];
  const currentMonth = new Date().getMonth() + 1;
  const isExamMonth = EXAM_FEE_MONTHS.includes(currentMonth);

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Month-end dues manager state
  const [showDuesManager, setShowDuesManager] = useState(false);

  useEffect(() => {
    fetchStudents();
    fetchTodaysCollection();
    fetchFeeAbortTime();
  }, []);

  // Close active filter dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showActiveDropdown && !e.target.closest('.active-filter-container')) {
        setShowActiveDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActiveDropdown]);

  const fetchFeeAbortTime = async () => {
    try {
      const { data, error } = await supabase
        .from('system_properties')
        .select('value')
        .eq('key', 'Fee Abort Time')
        .single();

      if (!error && data && data.value) {
        const seconds = parseInt(data.value, 10);
        if (!isNaN(seconds) && seconds > 0) {
          setFeeAbortTime(seconds);
          setCountdown(seconds);
        }
      }
    } catch (err) {
      console.error('Error fetching Fee Abort Time:', err);
    }
  };

  useEffect(() => {
    if (!loading && students.length > 0) {
      const payRno = searchParams.get('payRno');
      if (payRno) {
        const student = students.find(s => String(s.Rno) === payRno);
        if (student) {
          openPaymentModal(student);
        }
        searchParams.delete('payRno');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [loading, students]);

  useEffect(() => {
    filterStudents();
  }, [students, searchTerm, classFilter, activeFilter]);

  const fetchTodaysCollection = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();
      
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('amount_paid')
        .gte('created_at', todayISO);
      
      if (error) throw error;
      
      const total = data.reduce((sum, transaction) => sum + (parseFloat(transaction.amount_paid) || 0), 0);
      setTodaysCollection(Math.round(total));
    } catch (error) {
      console.error('Error fetching today\'s collection:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      
      // Fetch students with their private details (FeeDues)
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('*')
        .order('Class', { ascending: true })
        .order('Name', { ascending: true });

      if (studentsError) throw studentsError;

      // Fetch private details for all students
      const { data: privateData, error: privateError } = await supabase
        .from('students_private_details')
        .select('Rno, FeeDues, "Default Discount"');

      if (privateError) throw privateError;

      // Merge data
      const mergedData = studentsData.map(student => {
        const privateInfo = privateData.find(p => p.Rno === student.Rno);
        return {
          ...student,
          FeeDues: privateInfo?.FeeDues || 0,
          'Default Discount': privateInfo?.['Default Discount'] || 0
        };
      });

      setStudents(mergedData);
      
      // Extract unique classes
      const classes = [...new Set(mergedData.map(s => s.Class).filter(Boolean))].sort();
      setAvailableClasses(classes);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterStudents = () => {
    let filtered = [...students];

    // Apply active filter
    if (activeFilter === 'active') {
      filtered = filtered.filter(student => student.Active !== false);
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter(student => student.Active === false);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(student =>
        student.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.Rno?.toString().includes(searchTerm) ||
        student.Class?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply class filter
    if (classFilter !== 'all') {
      filtered = filtered.filter(student => student.Class === classFilter);
    }

    setFilteredStudents(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];
    if (valA == null) return sortDirection === 'asc' ? -1 : 1;
    if (valB == null) return sortDirection === 'asc' ? 1 : -1;
    if (typeof valA === 'string') {
      const cmp = valA.localeCompare(valB, undefined, { sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    }
    if (typeof valA === 'number') {
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    }
    return 0;
  });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
  };

  const closeNotification = () => {
    setNotification({ show: false, message: '', type: 'success' });
  };

  const fetchTransactionHistory = async (studentRno) => {
    try {
      setLoadingHistory(true);
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('student_rno', studentRno)
        .gte('created_at', twelveMonthsAgo.toISOString())
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setTransactionHistory(data || []);
    } catch (error) {
      console.error('Error fetching transaction history:', error);
      setTransactionHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openPaymentModal = async (student) => {
    setSelectedStudent(student);
    setPaymentAmount('');
    setCurrentMonthFee(0);
    setExaminationFee(0);
    setTransportFee(0);
    setPaymentMethod('Cash');
    setSendToWhatsApp(false); // Default unchecked for Cash
    setReceiptDate(new Date().toISOString().slice(0, 10));
    setOtherFees([]);
    setAlreadyPaidMonthly(false);
    setAlreadyPaidExam(false);
    setAlreadyPaidTransport(false);
    setShowPaymentModal(true);
    setLoadingFees(true);
    
    // Fetch transaction history
    fetchTransactionHistory(student.Rno);
    
    // Check if student already paid this month
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();
    
    let paidMonthly = false;
    let paidExam = false;
    let paidTransport = false;
    
    try {
      const { data: monthTxns, error: txnError } = await supabase
        .from('payment_transactions')
        .select('current_month_fee, examination_fee, transport_fee')
        .eq('student_rno', student.Rno)
        .gte('created_at', monthStart)
        .lte('created_at', monthEnd);
      
      if (!txnError && monthTxns && monthTxns.length > 0) {
        paidMonthly = monthTxns.some(t => (t.current_month_fee || 0) > 0);
        paidExam = monthTxns.some(t => (t.examination_fee || 0) > 0);
        paidTransport = monthTxns.some(t => (t.transport_fee || 0) > 0);
        setAlreadyPaidMonthly(paidMonthly);
        setAlreadyPaidExam(paidExam);
        setAlreadyPaidTransport(paidTransport);
      }
    } catch (error) {
      console.error('Error checking this month payments:', error);
    }
    
    // Fetch class fee from classes table
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('fee, exam_fee, transport_fee')
        .eq('class_name', student.Class)
        .single();
      
      if (error) {
        console.error('Error fetching class fee:', error);
        console.log('Student class:', student.Class);
      } else if (data) {
        setCurrentMonthFee(paidMonthly ? 0 : (parseFloat(data.fee) || 0));
        setExaminationFee(paidExam ? 0 : (isExamMonth ? (parseFloat(data.exam_fee) || 0) : 0));
        if (student.Transport) {
          const studentTransportFee = parseFloat(student['Transport Fee']) || 0;
          const classTransportFee = parseFloat(data.transport_fee) || 0;
          setTransportFee(paidTransport ? 0 : (studentTransportFee > 0 ? studentTransportFee : classTransportFee));
        } else {
          setTransportFee(0);
        }
        setDiscount(parseFloat(student['Default Discount']) || 0);
      }
    } catch (error) {
      console.error('Error fetching class fee:', error);
    } finally {
      setLoadingFees(false);
    }
  };

  const closePaymentModal = () => {
    // Clean up countdown timer if exists
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    
    setShowPaymentModal(false);
    setSelectedStudent(null);
    setPaymentAmount('');
    setCurrentMonthFee(0);
    setExaminationFee(0);
    setTransportFee(0);
    setDiscount(0);
    setOtherFees([]);
    setIsProcessing(false);
    setIsPendingTransaction(false);
    setPendingTransactionData(null);
    setCountdown(feeAbortTime);
    setPaymentMethod('Cash');
    setSendToWhatsApp(true);
  };

  const handlePaymentSubmit = () => {
    if (!selectedStudent || !paymentAmount) {
      showNotification('Please enter a payment amount', 'error');
      return;
    }

    const amount = parseFloat(paymentAmount);
    
    if (isNaN(amount)) {
      showNotification('Please enter a valid number', 'error');
      return;
    }

    // Calculate total amount that needs to be paid
    const effectiveTransportFee = selectedStudent.Transport ? transportFee : 0;
    const otherFeesTotal = otherFees.reduce((sum, f) => sum + (parseFloat(f.value) || 0), 0);
    const baseTotal = calculateTotalDue(selectedStudent.FeeDues, currentMonthFee, examinationFee, effectiveTransportFee, 0);
    const totalDueAmount = Math.max(0, baseTotal + otherFeesTotal - discount);
    
    // Calculate new dues after payment
    const newDues = amount - totalDueAmount;
    
    // Store transaction data
    const transactionData = {
      student_rno: selectedStudent.Rno,
      fee_dues: Math.round(selectedStudent.FeeDues || 0),
      current_month_fee: Math.round(currentMonthFee),
      transport_fee: Math.round(effectiveTransportFee),
      examination_fee: Math.round(examinationFee),
      discount: discount > 0 ? -Math.round(discount) : 0,
      other: otherFees.filter(f => f.label && f.value).map(f => ({ label: f.label, value: Math.round(parseFloat(f.value) || 0) })),
      miscellaneous_fees: [],
      total_amount: Math.round(totalDueAmount),
      amount_paid: Math.round(amount),
      new_dues: Math.round(newDues),
      payment_method: paymentMethod,
      created_by: user?.email || 'admin',
      created_at: new Date(receiptDate + 'T' + new Date().toTimeString().slice(0, 8)).toISOString()
    };
    
    setPendingTransactionData(transactionData);
    setIsPendingTransaction(true);
    setIsProcessing(true);
    setCountdown(feeAbortTime);
    
    // Start countdown
    let timeLeft = feeAbortTime;
    const timer = setInterval(() => {
      timeLeft -= 1;
      setCountdown(timeLeft);
      
      if (timeLeft <= 0) {
        clearInterval(timer);
        // Process the transaction after 5 seconds
        processTransaction(transactionData, newDues);
      }
    }, 1000);
    
    setCountdownTimer(timer);
  };
  
  // Send payment confirmation to WhatsApp
  const sendPaymentWhatsApp = async (transactionId, transactionData, newDues, privateDetails) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';
      
      // Calculate dues and advance
      let dues = 0;
      let advance = 0;
      if (newDues < 0) {
        dues = Math.abs(newDues);
      } else if (newDues > 0) {
        advance = newDues;
      }
      
      // Generate receipt URL with student info as query parameters
      const studentName = encodeURIComponent(selectedStudent.Name);
      const rollNumber = encodeURIComponent(selectedStudent.Rno);
      const studentClass = encodeURIComponent(selectedStudent.Class || 'N/A');
      const fatherName = encodeURIComponent(privateDetails?.["Father's Name (Son)"] || 'N/A');
      const motherName = encodeURIComponent(privateDetails?.["Mother's Name (Son)"] || 'N/A');
      const receiptUrl = `academic/transaction/${transactionId}?name=${studentName}&rno=${rollNumber}&class=${studentClass}&father=${fatherName}&mother=${motherName}`;
      
      // Collect phone numbers
      const phoneNumbers = [];
      if (privateDetails?.Mobile1) {
        phoneNumbers.push({ number: `91${privateDetails.Mobile1}`, label: 'Mobile1' });
      }
      if (privateDetails?.Mobile2) {
        phoneNumbers.push({ number: `91${privateDetails.Mobile2}`, label: 'Mobile2' });
      }
      
      if (phoneNumbers.length === 0) {
        console.log('⚠️ No phone numbers available');
        return;
      }
      
      console.log(`📤 Sending payment confirmation to ${phoneNumbers.length} number(s)`);
      
      // Send to each phone
      let successCount = 0;
      for (const phoneInfo of phoneNumbers) {
        try {
          console.log(`📤 Sending to ${phoneInfo.label}: ${phoneInfo.number}`);
          
          const response = await authFetch(`${API_BASE_URL}/api/whatsapp/send-payment-confirmation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              phoneNumber: phoneInfo.number,
              amount: Math.round(transactionData.amount_paid),
              studentName: selectedStudent.Name,
              dues,
              advance,
              receiptUrl
            })
          });
          
          if (response.ok) {
            console.log(`✅ Sent to ${phoneInfo.label}`);
            successCount++;
          } else {
            console.error(`❌ Failed to send to ${phoneInfo.label}`);
          }
          
          // Delay between sends
          if (phoneNumbers.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error(`❌ Error sending to ${phoneInfo.label}:`, error);
        }
      }
      
      if (successCount > 0) {
        const message = phoneNumbers.length > 1
          ? `Payment receipt sent to ${successCount} WhatsApp number(s)!`
          : 'Payment receipt sent to WhatsApp!';
        showNotification(message, 'success');
      }
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
    }
  };
  
  const processTransaction = async (transactionData, newDues) => {
    try {
      // Update FeeDues in students_private_details
      const { error: updateError } = await supabase
        .from('students_private_details')
        .update({ FeeDues: newDues })
        .eq('Rno', selectedStudent.Rno);

      if (updateError) throw updateError;
      
      // Insert transaction record and get inserted data
      const { data: insertedTransaction, error: insertError } = await supabase
        .from('payment_transactions')
        .insert([transactionData])
        .select()
        .single();

      if (insertError) throw insertError;

      // Update local state
      setStudents(students.map(student =>
        student.Rno === selectedStudent.Rno
          ? { ...student, FeeDues: newDues }
          : student
      ));

      showNotification(`Payment of ₹${transactionData.amount_paid} recorded successfully for ${selectedStudent.Name}!`, 'success');
      
      // Send WhatsApp if checkbox was checked
      if (sendToWhatsApp && insertedTransaction) {
        // Fetch private details for phone numbers
        const { data: privateDetails } = await supabase
          .from('students_private_details')
          .select('Mobile1, Mobile2')
          .eq('Rno', selectedStudent.Rno)
          .single();
        
        if (privateDetails) {
          await sendPaymentWhatsApp(insertedTransaction.id, transactionData, newDues, privateDetails);
        }
      }
      
      fetchTodaysCollection(); // Refresh today's collection
      // Refresh transaction history before closing
      if (selectedStudent) {
        await fetchTransactionHistory(selectedStudent.Rno);
      }
      resetPaymentModal();
    } catch (error) {
      console.error('Error processing payment:', error);
      showNotification('Failed to process payment. Please try again.', 'error');
      setIsProcessing(false);
      setIsPendingTransaction(false);
    }
  };
  
  const handleAbortPayment = () => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    
    showNotification('Payment aborted. No transaction recorded.', 'error');
    resetPaymentModal();
  };
  
  const resetPaymentModal = () => {
    setIsPendingTransaction(false);
    setIsProcessing(false);
    setPendingTransactionData(null);
    setCountdown(feeAbortTime);
    setPaymentMethod('Cash');
    setSendToWhatsApp(true);
    closePaymentModal();
  };

  const getFeeStats = () => {
    const total = filteredStudents.length;
    
    // Calculate total pending amount (sum of all negative FeeDues)
    const totalPending = filteredStudents.reduce((sum, student) => {
      const dues = parseFloat(student.FeeDues) || 0;
      return dues < 0 ? sum + Math.abs(dues) : sum;
    }, 0);
    
    // Calculate total advance/paid amount (sum of all positive FeeDues)
    const totalAdvance = filteredStudents.reduce((sum, student) => {
      const dues = parseFloat(student.FeeDues) || 0;
      return dues > 0 ? sum + dues : sum;
    }, 0);
    
    return { 
      total, 
      paid: Math.round(totalAdvance), 
      pending: Math.round(totalPending), 
      todaysCollection 
    };
  };

  const stats = getFeeStats();

  if (loading) {
    return (
      <div className="fees-container">
        <div className="fees-header">
          <h1><FaRupeeSign /> Fees Management</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fees-container">
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />

      <div className="fees-header">
        <h1><FaRupeeSign /> Fees Management</h1>
        <p className="subtitle">Manage and track student fee payments</p>
      </div>

      {/* Stats Cards */}
      <div className="fees-stats">
        <div className="stat-card total-card">
          <div className="stat-icon">
            <FaUserGraduate />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Students</div>
          </div>
        </div>

        <div className="stat-card paid-card">
          <div className="stat-icon">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <div className="stat-value">₹{stats.paid}</div>
            <div className="stat-label">Advance Payments</div>
          </div>
        </div>

        <div className="stat-card pending-card">
          <div className="stat-icon">
            <FaTimesCircle />
          </div>
          <div className="stat-content">
            <div className="stat-value">₹{stats.pending}</div>
            <div className="stat-label">Fees Pending</div>
          </div>
        </div>

        <div className="stat-card percentage-card">
          <div className="stat-icon">
            <FaCalendarAlt />
          </div>
          <div className="stat-content">
            <div className="stat-value">₹{stats.todaysCollection}</div>
            <div className="stat-label">Today's Collection</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="fees-filters">
        <div className="fees-filters-left">
          <div className="filter-group">
            <FaSearch className="filter-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, roll no, or class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-group filter-group-small">
            <FaFilter className="filter-icon" />
            <select
              className="fees-filter-select"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="all">All Classes</option>
              {availableClasses.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          {/* Active/Inactive Filter */}
          <div className="active-filter-container" style={{ position: 'relative' }}>
            <button
              className={`active-filter-btn ${activeFilter !== 'active' ? 'filtered' : ''}`}
              onClick={() => setShowActiveDropdown(!showActiveDropdown)}
            >
              <i className={`fas ${activeFilter === 'active' ? 'fa-toggle-on' : activeFilter === 'inactive' ? 'fa-toggle-off' : 'fa-users'}`}></i>
              <span>{activeFilter === 'active' ? 'Active' : activeFilter === 'inactive' ? 'Inactive' : 'All'}</span>
              <i className={`fas fa-chevron-${showActiveDropdown ? 'up' : 'down'}`}></i>
            </button>
            {showActiveDropdown && (
              <div className="active-filter-dropdown">
                {[
                  { value: 'active', label: 'Active', icon: 'fa-toggle-on' },
                  { value: 'inactive', label: 'Inactive', icon: 'fa-toggle-off' },
                  { value: 'all', label: 'All', icon: 'fa-users' },
                ].map(opt => (
                  <div
                    key={opt.value}
                    className={`active-filter-option ${activeFilter === opt.value ? 'selected' : ''}`}
                    onClick={() => { setActiveFilter(opt.value); setShowActiveDropdown(false); }}
                  >
                    <i className={`fas ${opt.icon}`}></i>
                    <span>{opt.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="fees-filters-right">
          <button 
            className="transactions-btn"
            onClick={() => navigate('/academic/transactions')}
          >
            <FaHistory /> All Transactions
          </button>

          <button 
            className="transactions-btn dues-manager-btn"
            onClick={() => setShowDuesManager(true)}
          >
            <FaCog /> Month-End Dues
          </button>
        </div>
      </div>

      {/* Students Table */}
      <div className="fees-table-container">
        {filteredStudents.length === 0 ? (
          <div className="no-data">
            <p>No students found matching your filters</p>
          </div>
        ) : (
          <table className="fees-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th className="sortable-th" onClick={() => handleSort('Rno')}>
                  Roll No {sortField === 'Rno' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}
                </th>
                <th className="sortable-th" onClick={() => handleSort('Name')}>
                  Name {sortField === 'Name' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}
                </th>
                <th className="sortable-th" onClick={() => handleSort('Class')}>
                  Class {sortField === 'Class' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}
                </th>
                <th className="sortable-th" onClick={() => handleSort('FeeDues')}>
                  Fee Dues (₹) {sortField === 'FeeDues' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedStudents.map((student) => {
                const feeDues = parseFloat(student.FeeDues) || 0;
                const isDue = feeDues < 0;
                const isAdvance = feeDues > 0;
                
                return (
                  <tr 
                    key={student.Rno} 
                    className={isDue ? 'dues-row' : isAdvance ? 'advance-row' : 'cleared-row'}
                  >
                    <td>
                      <button
                        className="redirect-btn"
                        title="Open in Student List"
                        onClick={() => window.open(`/students?editRno=${student.Rno}`, '_blank')}
                      >
                        <FaExternalLinkAlt />
                      </button>
                    </td>
                    <td>{student.Rno}</td>
                    <td className="student-name">{student.Name}</td>
                    <td>
                      <span className="class-badge">{student.Class || 'N/A'}</span>
                    </td>
                    <td>
                      <span className={`dues-badge ${
                        isDue ? 'dues-negative' : 
                        isAdvance ? 'dues-positive' : 
                        'dues-zero'
                      }`}>
                        {feeDues < 0 && '−'}
                        {feeDues > 0 && '+'}
                        ₹{Math.round(Math.abs(feeDues))}
                      </span>
                    </td>
                    <td>
                      <button
                        className="pay-btn"
                        onClick={() => openPaymentModal(student)}
                      >
                        <FaRupeeSign /> Pay
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedStudent && (
        <div className="modal-overlay" onClick={closePaymentModal}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FaRupeeSign /> Record Payment</h2>
              <button className="close-btn" onClick={closePaymentModal}>
                &times;
              </button>
            </div>

            <div className="modal-body modal-body-expanded">
              <div className="payment-form-column">
              <div className="student-details-section">
                <h3>Student Details</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Roll No:</span>
                    <span className="detail-value">{selectedStudent.Rno}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedStudent.Name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Class:</span>
                    <span className="detail-value">{selectedStudent.Class || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Current Dues:</span>
                    <span className={`detail-value dues-amount ${
                      selectedStudent.FeeDues < 0 ? 'negative' : 
                      selectedStudent.FeeDues > 0 ? 'positive' : 'zero'
                    }`}>
                      {selectedStudent.FeeDues < 0 && '−'}
                      {selectedStudent.FeeDues > 0 && '+'}
                      ₹{Math.round(Math.abs(selectedStudent.FeeDues || 0))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="fee-breakdown-section">
                <h3>Fee Breakdown <span style={{ fontWeight: 400, fontSize: '0.85em', color: '#666' }}>on </span>
                  <input
                    type="date"
                    className="receipt-date-input"
                    value={receiptDate}
                    onChange={(e) => setReceiptDate(e.target.value)}
                    min={currentMonthStart}
                    max={currentMonthEnd}
                  />
                </h3>
                <div className="breakdown-list">
                  <div className="breakdown-item">
                    <span className="breakdown-label">Previous Dues:</span>
                    <span className={`breakdown-value ${
                      selectedStudent.FeeDues < 0 ? 'negative' : 
                      selectedStudent.FeeDues > 0 ? 'positive' : 'zero'
                    }`}>
                      {selectedStudent.FeeDues < 0 && '−'}
                      {selectedStudent.FeeDues > 0 && '+'}
                      ₹{Math.round(Math.abs(selectedStudent.FeeDues || 0))}
                    </span>
                  </div>
                  <div className="breakdown-item editable-item">
                    <div className="breakdown-label-wrap">
                      <span className="breakdown-label">Current Month Fee:</span>
                      {alreadyPaidMonthly && <span className="already-paid-badge">Paid this month</span>}
                    </div>
                    <div className="editable-value">
                      {loadingFees && <i className="fas fa-spinner fa-spin" style={{ fontSize: '16px', color: '#4361ee', marginRight: '6px' }}></i>}
                      <span className="rupee-symbol">₹</span>
                      <input
                        type="number"
                        step="1"
                        className="fee-input"
                        value={currentMonthFee}
                        onChange={(e) => setCurrentMonthFee(parseFloat(e.target.value) || 0)}
                        disabled={isPendingTransaction}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item editable-item">
                    <div className="breakdown-label-wrap">
                      <span className="breakdown-label">Examination Fee:</span>
                      {alreadyPaidExam && <span className="already-paid-badge">Paid this month</span>}
                      {!isExamMonth && <span className="already-paid-badge" style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}>Not an examination month</span>}
                      {isExamMonth && !alreadyPaidExam && <span className="already-paid-badge" style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}>Examination month</span>}
                    </div>
                    <div className="editable-value">
                      {loadingFees && <i className="fas fa-spinner fa-spin" style={{ fontSize: '16px', color: '#4361ee', marginRight: '6px' }}></i>}
                      <span className="rupee-symbol">₹</span>
                      <input
                        type="number"
                        step="1"
                        className="fee-input"
                        value={examinationFee}
                        onChange={(e) => setExaminationFee(parseFloat(e.target.value) || 0)}
                        disabled={isPendingTransaction}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item editable-item">
                    <div className="breakdown-label-wrap">
                      <span className="breakdown-label">Transport Fee:</span>
                      {selectedStudent.Transport && alreadyPaidTransport && <span className="already-paid-badge">Paid this month</span>}
                      {!selectedStudent.Transport && <span className="already-paid-badge" style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}>Not enrolled in transport</span>}
                    </div>
                    <div className="editable-value">
                      {loadingFees && selectedStudent.Transport && <i className="fas fa-spinner fa-spin" style={{ fontSize: '16px', color: '#4361ee', marginRight: '6px' }}></i>}
                      <span className="rupee-symbol">₹</span>
                      <input
                        type="number"
                        step="1"
                        className="fee-input"
                        value={transportFee}
                        onChange={(e) => setTransportFee(parseFloat(e.target.value) || 0)}
                        disabled={isPendingTransaction || !selectedStudent.Transport}
                        style={!selectedStudent.Transport ? { backgroundColor: '#f9fafb', color: '#9ca3af', cursor: 'not-allowed' } : {}}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item editable-item">
                    <span className="breakdown-label">Discount:</span>
                    <div className="editable-value">
                      <span className="rupee-symbol">-₹</span>
                      <input
                        type="number"
                        step="1"
                        className="fee-input"
                        value={discount}
                        onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                        disabled={isPendingTransaction}
                      />
                    </div>
                  </div>
                  {/* Other Fees */}
                  <div className="breakdown-item other-fees-section">
                    <div className="other-fees-header">
                      <span className="breakdown-label">Other Fees:</span>
                      <button
                        type="button"
                        className="other-fee-add-btn"
                        onClick={() => setOtherFees([...otherFees, { label: '', value: '' }])}
                        disabled={isPendingTransaction}
                        title="Add other fee"
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                    {otherFees.map((fee, idx) => (
                      <div className="other-fee-row" key={idx}>
                        <input
                          type="text"
                          className="other-fee-label-input"
                          placeholder="Label"
                          value={fee.label}
                          onChange={(e) => {
                            const updated = [...otherFees];
                            updated[idx] = { ...updated[idx], label: e.target.value };
                            setOtherFees(updated);
                          }}
                          disabled={isPendingTransaction}
                        />
                        <div className="editable-value other-fee-value">
                          <span className="rupee-symbol">₹</span>
                          <input
                            type="number"
                            step="1"
                            className="fee-input"
                            placeholder="0"
                            value={fee.value}
                            onChange={(e) => {
                              const updated = [...otherFees];
                              updated[idx] = { ...updated[idx], value: e.target.value };
                              setOtherFees(updated);
                            }}
                            disabled={isPendingTransaction}
                          />
                        </div>
                        <button
                          type="button"
                          className="other-fee-remove-btn"
                          onClick={() => setOtherFees(otherFees.filter((_, i) => i !== idx))}
                          disabled={isPendingTransaction}
                          title="Remove"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="breakdown-item total-item">
                    <span className="breakdown-label">Total Amount to Pay:</span>
                    <span className="breakdown-value total-value">
                      ₹{Math.round(Math.max(0, calculateTotalDue(selectedStudent.FeeDues, currentMonthFee, examinationFee, selectedStudent.Transport ? transportFee : 0, 0) + otherFees.reduce((sum, f) => sum + (parseFloat(f.value) || 0), 0) - discount))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="payment-input-section">
                {/* Payment Method Selector */}
                <div className="payment-method-section">
                  <label className="section-label">Payment Method</label>
                  <div className="payment-method-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Cash"
                        checked={paymentMethod === 'Cash'}
                        onChange={(e) => {
                          setPaymentMethod(e.target.value);
                          setSendToWhatsApp(false); // Uncheck for Cash
                        }}
                        disabled={isPendingTransaction}
                      />
                      <span className="radio-label">Cash</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Online"
                        checked={paymentMethod === 'Online'}
                        onChange={(e) => {
                          setPaymentMethod(e.target.value);
                          setSendToWhatsApp(true); // Check for Online
                        }}
                        disabled={isPendingTransaction}
                      />
                      <span className="radio-label">Online</span>
                    </label>
                  </div>
                </div>

                {/* Payment Amount */}
                <div className="amount-input-wrapper">
                  <label htmlFor="paymentAmount">
                    <FaRupeeSign /> Payment Amount
                  </label>
                  <input
                    id="paymentAmount"
                    type="number"
                    step="1"
                    placeholder="Enter amount"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isPendingTransaction) {
                        handlePaymentSubmit();
                      }
                    }}
                    disabled={isPendingTransaction}
                    autoFocus
                  />
                </div>

                {/* WhatsApp Notification Checkbox */}
                <div className="whatsapp-option">
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      checked={sendToWhatsApp}
                      onChange={(e) => setSendToWhatsApp(e.target.checked)}
                      disabled={isPendingTransaction}
                    />
                    <span className="checkbox-label">
                      Send receipt to WhatsApp
                    </span>
                  </label>
                </div>
              </div>
              </div>

              <div className="transaction-history-column">
                <div className="history-header">
                  <h3><FaReceipt /> Transaction History</h3>
                  <span className="history-count">{transactionHistory.length} transactions</span>
                </div>
                {loadingHistory ? (
                  <div className="history-loading">
                    <div className="loading-spinner small"></div>
                    <p>Loading...</p>
                  </div>
                ) : transactionHistory.length === 0 ? (
                  <div className="history-empty">
                    <FaHistory className="empty-icon" />
                    <p>No transactions in last 12 months</p>
                  </div>
                ) : (
                  <div className="history-list">
                    {transactionHistory.map((txn, index) => {
                      const txnDate = new Date(txn.created_at);
                      const dateStr = txnDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                      const timeStr = txnDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                      const newDues = txn.new_dues || 0;
                      
                      return (
                        <div className="history-item" key={txn.id || index}>
                          <div className="history-item-top">
                            <div className="history-date">
                              <span className="date-text">{dateStr}</span>
                              <span className="time-text">{timeStr}</span>
                            </div>
                            <span className="history-amount">₹{Math.round(txn.amount_paid)}</span>
                          </div>
                          <div className="history-item-details">
                            <div className="history-detail-row">
                              <span>Monthly: ₹{Math.round(txn.current_month_fee || 0)}</span>
                              <span>Exam: ₹{Math.round(txn.examination_fee || 0)}</span>
                              <span>Transport: ₹{Math.round(txn.transport_fee || 0)}</span>
                            </div>
                            <div className="history-detail-row">
                              <span>Prev Dues: ₹{Math.round(txn.fee_dues || 0)}</span>
                              {txn.discount ? <span>Discount: ₹{Math.round(Math.abs(txn.discount))}</span> : null}
                              {txn.other && txn.other.length > 0 && (
                                <div className="history-other-pills">
                                  {txn.other.map((item, i) => (
                                    <span key={i} className="other-fee-pill">{item.label}: ₹{Math.abs(item.value)}</span>
                                  ))}
                                </div>
                              )}
                              <span className={`history-new-dues ${newDues < 0 ? 'negative' : newDues > 0 ? 'positive' : ''}`}>
                                After: {newDues < 0 ? '−' : newDues > 0 ? '+' : ''}₹{Math.round(Math.abs(newDues))}
                              </span>
                            </div>
                          </div>
                          <div className="history-item-footer">
                            <span className={`history-method ${txn.payment_method?.toLowerCase()}`}>{txn.payment_method || 'N/A'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              {!isPendingTransaction ? (
                <>
                  <button 
                    className="btn-cancel" 
                    onClick={closePaymentModal}
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn-submit" 
                    onClick={handlePaymentSubmit}
                    disabled={isProcessing || !paymentAmount}
                  >
                    Submit Payment
                  </button>
                </>
              ) : (
                <>
                  <div className="countdown-info">
                    <span className="countdown-text">
                      Transaction will be recorded in <strong>{countdown}</strong> seconds
                    </span>
                  </div>
                  <button 
                    className="btn-abort" 
                    onClick={handleAbortPayment}
                  >
                    Abort ({countdown}s)
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Month-End Dues Manager Modal */}
      <MonthEndDuesManager
        show={showDuesManager}
        onClose={() => { setShowDuesManager(false); fetchStudents(); }}
        onNotify={(message, type) => showNotification(message, type)}
      />
    </div>
  );
};

export default Fees;
